import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeftIcon,
  CalendarIcon,
  CheckCircleIcon,
  XCircleIcon,
  ChartBarIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

const StudyProgramHistory = () => {
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  useEffect(() => {
    fetchProgramHistory();
  }, []);

  const fetchProgramHistory = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/study-programs/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setPrograms(response.data.data);
      }
    } catch (err) {
      console.error('Program geçmişi yükleme hatası:', err);
      setError('Program geçmişi yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
            <CheckCircleIcon className="h-4 w-4 mr-1" />
            Tamamlandı
          </span>
        );
      case 'cancelled':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
            <XCircleIcon className="h-4 w-4 mr-1" />
            İptal Edildi
          </span>
        );
      case 'active':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
            <ClockIcon className="h-4 w-4 mr-1" />
            Aktif
          </span>
        );
      default:
        return null;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const getPriorityStats = (tasks) => {
    const stats = { high: 0, medium: 0, low: 0 };
    tasks.forEach(task => {
      if (stats.hasOwnProperty(task.priority)) {
        stats[task.priority]++;
      }
    });
    return stats;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Geçmiş programlar yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/study-program')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4 transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Aktif Programa Dön
          </button>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                  Program Geçmişi
                </h1>
                <p className="text-gray-600">
                  Geçmiş çalışma programlarını incele ve performansını analiz et
                </p>
              </div>
              <CalendarIcon className="h-12 w-12 text-blue-600" />
            </div>
          </div>
        </div>

        {/* Programs List */}
        {error ? (
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <XCircleIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <p className="text-gray-600">{error}</p>
          </div>
        ) : programs.length === 0 ? (
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <CalendarIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Henüz Geçmiş Program Yok
            </h2>
            <p className="text-gray-600">
              Tamamlanan veya iptal edilen programların burada görünecek.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {programs.map(program => {
              const priorityStats = getPriorityStats(program.tasks);
              const completedTasks = program.tasks.filter(t => t.completed).length;

              return (
                <div key={program.id} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900 mb-1">
                        {program.exam?.examName || 'Deneme'}
                      </h2>
                      <p className="text-gray-600">
                        Hafta {program.weekNumber} • {formatDate(program.startDate)} - {formatDate(program.endDate)}
                      </p>
                    </div>
                    {getStatusBadge(program.status)}
                  </div>

                  {/* Progress Bar */}
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">Tamamlanma</span>
                      <span className="text-sm font-medium text-gray-900">
                        {completedTasks} / {program.tasks.length} Görev
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${
                          program.status === 'completed'
                            ? 'bg-green-600'
                            : program.status === 'cancelled'
                            ? 'bg-red-600'
                            : 'bg-blue-600'
                        }`}
                        style={{ width: `${program.completionPercentage || 0}%` }}
                      ></div>
                    </div>
                    <p className="text-right mt-1 text-sm text-gray-600">
                      %{Math.round(program.completionPercentage || 0)}
                    </p>
                  </div>

                  {/* Stats Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-gray-50 rounded-lg p-3">
                      <div className="flex items-center mb-1">
                        <ChartBarIcon className="h-4 w-4 text-gray-600 mr-2" />
                        <span className="text-xs text-gray-600">Toplam Görev</span>
                      </div>
                      <p className="text-xl font-bold text-gray-900">{program.tasks.length}</p>
                    </div>

                    <div className="bg-red-50 rounded-lg p-3">
                      <div className="flex items-center mb-1">
                        <span className="text-xs text-red-600 font-medium">Yüksek Öncelik</span>
                      </div>
                      <p className="text-xl font-bold text-red-700">{priorityStats.high}</p>
                    </div>

                    <div className="bg-yellow-50 rounded-lg p-3">
                      <div className="flex items-center mb-1">
                        <span className="text-xs text-yellow-600 font-medium">Orta Öncelik</span>
                      </div>
                      <p className="text-xl font-bold text-yellow-700">{priorityStats.medium}</p>
                    </div>

                    <div className="bg-green-50 rounded-lg p-3">
                      <div className="flex items-center mb-1">
                        <span className="text-xs text-green-600 font-medium">Düşük Öncelik</span>
                      </div>
                      <p className="text-xl font-bold text-green-700">{priorityStats.low}</p>
                    </div>
                  </div>

                  {/* View Details Button */}
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <button
                      onClick={() => navigate(`/study-program/${program.id}`)}
                      className="text-blue-600 hover:text-blue-700 font-medium text-sm"
                    >
                      Detayları Görüntüle →
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default StudyProgramHistory;
